import { Shot } from '../../page/landing-layout/tool-pages/product-shots';
import { featureShots, ProductFeature, ProductPictures, ProductScene, ProductStore, sceneShot, sceneSrc, ShowcaseConfig } from './product-features.data';

/** Tidy Desktop's raw screens (macos-tidy-desktop/.dev/site-compose.py from .dev/shots). */
export const TIDY_DESKTOP_PICTURES: ProductPictures = { dir: './assets/product-lines/tools/tidy-desktop/', prefix: 'td-' };

const desktop = ( key: string, name: string, alt: string ): ProductScene => ( { key, name, alt, width: 1600, height: 1039 } );
const theme = ( key: string, name: string, alt: string ): ProductScene => ( { key: `theme-${ key }`, name, label: name, alt, width: 900, height: 584 } );

// The App Store pictures taken apart (owner, 2026-10-10: as Dock Town): their headline is the
// page's own text, their screens are the raw captures they were made from (.dev/shots), each
// enlargeable. Shown on the home (app-product-showcase, one at a time) and on the Tidy Desktop
// page (all of them, under the stage): before and after, AI grouping, your own collections, the
// themes, the owner's order (2026-10-10; the Lightweight menu bar picture left the site 2026-10-19).
export const TIDY_DESKTOP_FEATURES: ProductFeature[] = [
  {
    key: 'tidy', chip: 'Before and After', accent: 'On-Device AI', rest: 'Tidies Your Desktop', layout: 'one',
    line: 'Before and after on one desktop: every file sorted into collections, and nothing leaves your Mac',
    description: 'Open Tidy Desktop and Apple\'s on-device AI looks at every file on your desktop and gathers it into a collection by what it is for: a trip, a project, invoices, school. Collections appear one by one as it works, and new files drop into the right one by themselves. Collections are a view, not folders, so the files stay where they are in your Desktop folder.',
    // one desktop, cluttered on the left and tidy on the right (owner 2026-10-10: the point of the
    // picture is the comparison in one frame, never two pictures)
    scenes: [ desktop( 'before-after', 'Before and After', 'One Mac desktop split down the middle: on the left covered in files, screenshots, PDFs and images, on the right the same files in Tidy Desktop collections: Party, CS Lecture, Invoices, Turkey, Spain, Codigrate, Japan, Italy and more' ) ],
  },
  {
    key: 'grouping', chip: 'AI Grouping', accent: 'AI Grouping', rest: 'by Meaning', layout: 'one',
    line: 'Files grouped by what they are for: a trip, a project, invoices',
    description: 'The model never sorts the desktop by itself: it answers one small question per file, what is this for, and plain code builds the collections from its answers. Files whose names share a word become one project, the rest gather by topic, and screenshots, installers and photos go by kind. Its answers are kept, so a later regroup only asks about files it has not seen yet. Without Apple Intelligence, Tidy Desktop still tidies the desktop by kind.',
    scenes: [ desktop( 'ai-grouping', 'AI Grouping', 'Tidy Desktop\'s Grouping settings with AI Grouping on and Apple Intelligence ready on device, over a desktop in the Dublin theme' ) ],
  },
  // 1.1 (2026-10-28): third, as on the App Store (owner)
  {
    key: 'icons', chip: 'Codigrate Icons', accent: 'Codigrate Icons', rest: 'for Every File', layout: 'one',
    line: 'Every file in its collection\'s color, marked by its kind',
    description: 'Turn on Use Codigrate Icons in Settings and every file becomes a page in its collection\'s color with a symbol for its kind pressed in, the way Finder marks its own folders. Folders take their collection\'s color too, with a symbol for what they hold. Turn it off and Finder\'s own icons and previews come back.',
    scenes: [ desktop( 'codigrate-icons', 'Codigrate Icons', 'Tidy Desktop collections in the Fuji theme with Codigrate Icons on: every file a page in its collection\'s color with a symbol for its kind, beside Mount Fuji' ) ],
  },
  {
    key: 'describe', chip: 'Your Own Collections', accent: 'Describe', rest: 'Any Collection', layout: 'one',
    line: 'AI adds the files that fit it, now and from now on',
    description: 'Give a collection a name, or let the model name it, and say what it is for. It takes the files that fit, the files of the topic its name says and the files whose names have its words, now and as new ones arrive. A collection of your own stays even while it is empty.',
    scenes: [ desktop( 'new-collection', 'New Collection', 'The New Collection prompt over a desktop in the Salda theme: a collection named Game Dev for the code, art and assets of an indie game' ) ],
  },
  {
    key: 'themes', chip: '20+ Themes', accent: 'Custom Themes', rest: 'for Your Collections', layout: 'four',
    line: 'The macOS look and more than 20 Codigrate themes; the wallpapers are not included',
    description: 'Collections wear the native macOS Liquid Glass look or one of the 24 Codigrate themes, from Aurora Borealis to Tokyo. Multicolor gives each collection one of the theme\'s five colors. The wallpapers in the pictures are not included.',
    scenes: [
      theme( 'macos', 'macOS', 'Tidy Desktop collections in Multicolor with the macOS look, on the macOS wallpaper' ),
      theme( 'aurora', 'Aurora Borealis', 'The same collections in the Aurora Borealis theme, dark, under the northern lights' ),
      theme( 'sakura', 'Sakura', 'The same collections in the Sakura theme, pink, beside a cherry tree in bloom' ),
      theme( 'everest', 'Everest', 'The same collections in the Everest theme, pale blue, beside a mountain at dawn' ),
    ],
  },
];

/** The features the home's showcase turns through: the App Store's pictures but the menu bar one. Sold on the site too (2026-10-14): while
 *  the store sells it, every slide shows Add to cart with the price, the Mac App Store button on the row under it (live 2026-10-27). */
const TIDY_DESKTOP_STORE: ProductStore = {
  label: 'Install from the Mac App Store',
  url: 'https://apps.apple.com/app/tidy-desktop-ai-organizer/id6818882449',
  macApp: true,
  event: 'App Install Clicked',
  attributes: { app: 'macos-tidy-desktop' },
};
export const TIDY_DESKTOP_HOME_FEATURES: ProductFeature[] = TIDY_DESKTOP_FEATURES
    .filter( f => f.home !== false )
    .map( f => ( { ...f, cartSku: 'tidy-desktop/macos', store: TIDY_DESKTOP_STORE } ) );

/** The page's file of a scene. */
export function tidyDesktopSceneSrc( scene: ProductScene ): string {
  return sceneSrc( TIDY_DESKTOP_PICTURES, scene );
}

/** The lightbox entry of a scene: the page's file, the full-size one, the name and the alt text. */
export function tidyDesktopShot( scene: ProductScene ): Shot {
  return sceneShot( TIDY_DESKTOP_PICTURES, scene );
}

/** Every scene of every feature as a lightbox entry, in order, so the arrows walk through them all. */
export function tidyDesktopShots( features: ProductFeature[] = TIDY_DESKTOP_FEATURES ): Shot[] {
  return featureShots( TIDY_DESKTOP_PICTURES, features );
}

/** The home's Tidy Desktop showcase (product-showcase.component.ts): what the app is, and its features. */
export const TIDY_DESKTOP_SHOWCASE: ShowcaseConfig = {
  key: 'tidy-desktop',
  shape: 'scene',
  eyebrowLogo: './assets/third-parties/logo/macos-apps.webp',
  eyebrow: 'macOS App',
  icon: './assets/product-lines/tools/macos-tidy-desktop.png',
  title: 'Tidy Desktop: AI Organizer',
  lead: 'The AI desktop organizer for your Mac',
  text: 'Apple\'s on-device AI sorts the files on your desktop into tidy collections by what they are for. New files find their place by themselves, your files stay where they are, and nothing leaves your Mac.',
  facts: [ 'macOS 26 Or Later', 'Using Apple Intelligence' ],
  link: '/plugins/macos/tidy-desktop',
  cta: 'Discover More',
  soon: 'Coming Soon to the Mac App Store',
  pickerLabel: 'What Tidy Desktop does',
  pictures: TIDY_DESKTOP_PICTURES,
  slides: TIDY_DESKTOP_HOME_FEATURES,
};
